import { signal } from '@preact/signals-react'
import type { AnalyticsBrowser } from '@segment/analytics-next'
import { getOneTrustConsent, setAssumedConsent } from '@susu/cookie'
import {
  getGA4ClientIdFromCookie,
  getGA4SessionDataFromCookie,
  resolveGA4MeasurementIdForEnvironment,
  setGA4MeasurementId,
} from '@susu/segment-common'

import {
  BUFFERED_DESTINATION_LIMIT,
  type BufferedSegmentDestination,
  createBufferedDestination,
  createSegmentDestination,
  type SegmentDestination,
  setAvoLoadedSignal,
} from './avo-destination'
import { reportBootStage } from './boot-health'
import {
  getGaCookieId,
  IDENTITY_KEY_ANONYMOUS,
  IDENTITY_KEY_KNOWN,
  IDENTITY_VALUE,
  setGaCookieId,
} from './constants'
import { isBrowser } from './env'
import { insertCookieTrackingScript } from './one-trust-cookie-banner'
import { setPageOwner } from './page-context'
import { integrationPlugin } from './plugins'
import { registerProductAdapter } from './product-adapter'
import { analytics, loadTransport } from './segment'
import {
  isProductionEnvironment,
  ONE_TRUST_DOMAIN_SCRIPT_ID,
  resolveSegmentWriteKey,
} from './sources'
import { currentCommonSystemProperties } from './system-properties'
import type {
  AnalyticsSurface,
  ApplicationAnalyticsOptions,
  ConsentPosture,
  InitializeAnalyticsOptions,
  RegisterAnalyticsOptions,
  UseAnalyticsOptions,
} from './types'
import {
  createPromiseFromSignal,
  errorHandler,
  setGALoadedSignal,
  validateApplicationId,
} from './utils'

/**
 * The consent an in-store colleague device runs under. There is no consumer
 * banner on those devices, so there is no OptanonConsent cookie to gate on and
 * the assumed set is declared here — once, typed and reviewable — instead of
 * being restated by each in-store package (one of which was omitting
 * `assumedConsent` altogether). A real OptanonConsent cookie still wins, and a
 * OneTrust host owning the page clears it entirely.
 */
const IN_STORE_CONSENT: ConsentPosture = {
  mode: 'ungated',
  justification: 'In-store client on a colleague device, no consumer PII',
  assumedConsent: {
    categoryPreferences: {
      C0001: true,
      C0002: true,
      C0003: true,
      C0004: true,
    },
  },
}

/**
 * The consent posture for a surface. Derived, never passed in: a website host
 * owns the OneTrust banner, an in-store colleague device has none.
 */
const resolveConsentPosture = (surface: AnalyticsSurface): ConsentPosture =>
  surface.placement === 'inStore'
    ? IN_STORE_CONSENT
    : {
        mode: 'oneTrust',
        oneTrustId: surface.oneTrustId ?? ONE_TRUST_DOMAIN_SCRIPT_ID,
      }

// Export analytics instance for external use
export { analytics } from './segment'

// Internal boot signals. Application logic must never wait on any of these —
// events fired before readiness are buffered, not dropped (FC-183 §7), which
// is why none of them are exported (FC-186 done-when 5).
const libraryLoaded = signal<boolean>(false)
const avoLoaded = signal<boolean>(false)
// Module-level export for the contract tests only — NOT re-exported by index.ts.
export const gaLoaded = signal<boolean | undefined>(undefined)
const library = signal<AnalyticsBrowser | undefined>(undefined)

/** Resolves once GA4's cookies settle (bounded) — internal identify trigger. */
const gaPromise = createPromiseFromSignal(gaLoaded)

// ── Module state — the two scopes of §3, deliberately separate (D1) ──────────
// Transport state is once-per-page; application state is once-per-application.
// Conflating them is the classic defect: a second application either
// double-loads the transport or silently skips its own Avo init.

/** Set synchronously when the host constructs the transport. */
let transportLoaded = false
/**
 * The consent posture the page is running under. Once a OneTrust host has
 * claimed the page this is sticky: a later `ungated` registration must never
 * be able to widen consent behind a real banner (D5).
 */
let pageConsentMode: 'oneTrust' | 'ungated' | undefined
/** The host's resolved Avo environment, shared with composed applications. */
let hostAvoEnv: 'dev' | 'prod' | undefined
/** Destinations of composed applications registered before the host initialized (§3). */
const pendingComposedDestinations: BufferedSegmentDestination[] = []
/** How long a composed registration may stay pending before it is reported. */
const COMPOSED_REGISTRATION_WARN_MS = 15000
/** Applications whose Avo client has been initialized on this page. */
const initializedApplications = new Set<string>()
/** Setters to push resolved GA identity into each application's Avo client (D3). */
const avoSystemPropertySetters = new Map<
  string,
  (properties: Record<string, unknown>) => void
>()

/**
 * GA4 identity, read from the cookies Segment's GA4 destination writes. Three
 * properties, no more: `gaClientId`, `gaSessionId`, `gaSessionNumber`.
 *
 * A field GA has not written yet is omitted, never substituted. The previous
 * `'unresolved'` marker travelled as `properties.ga_session_id`, and the GA4
 * Web destination maps that straight into gtag's `session_id` — so gtag wrote
 * the marker into `_ga_<id>` and every later read returned it. GA owns the
 * cookie; we only read it.
 *
 * The deprecated `clientId`/`sessionId` duplicates are gone. A generated
 * client that still declares them simply receives nothing for them.
 */
const currentGAIdentity = (): {
  gaClientId?: string
  gaSessionId?: string
  gaSessionNumber?: string
} => {
  const { sessionId, sessionNumber } = getGA4SessionDataFromCookie(
    getGaCookieId() ?? '',
  )
  const gaClientId = getGA4ClientIdFromCookie()

  return {
    ...(gaClientId ? { gaClientId } : {}),
    ...(sessionId ? { gaSessionId: sessionId } : {}),
    ...(sessionNumber ? { gaSessionNumber: sessionNumber } : {}),
  }
}

/**
 * Pushes GA identity into every registered Avo client once GA4's cookies have
 * resolved — or the bounded wait timed out, in which case the fields GA never
 * wrote are simply absent (D3). `gaLoaded` itself is bounded by the existing
 * 2000 ms wait in createGAdata; no second timeout.
 */
gaLoaded.subscribe(value => {
  if (value === undefined) {
    return
  }
  const identity = currentGAIdentity()
  for (const setter of avoSystemPropertySetters.values()) {
    try {
      setter(identity)
    } catch (error) {
      errorHandler(error as Error)
    }
  }
})

/**
 * Pushes system properties into one application's Avo client after boot — for
 * values that only resolve later, such as the page's locale, the order being
 * returned, or the signed-in customer.
 *
 * The application must already have registered (via {@link initializeAnalytics}
 * or {@link useAnalytics}) passing a `setAvoSystemProperties`; without one
 * there is nowhere to push to, which is a misconfiguration worth surfacing
 * rather than dropping silently. Non-fatal either way.
 *
 * @param applicationId - The application whose Avo client to update
 * @param properties - System properties to set; a partial set is fine
 */
export const updateSystemProperties = (
  applicationId: string,
  properties: Record<string, unknown>,
): void => {
  const setter = avoSystemPropertySetters.get(applicationId)

  if (!setter) {
    errorHandler(
      new Error(
        `[analytics-client] updateSystemProperties('${applicationId}') found no registered setter — the application has either not initialized yet or did not pass setAvoSystemProperties, so these properties are dropped.`,
      ),
    )
    return
  }

  try {
    setter(properties)
  } catch (error) {
    errorHandler(error as Error)
  }
}

/**
 * Registers one application's Avo client — the once-per-APPLICATION scope of
 * the boot flow (§3). Guarded per application id: the same application
 * registering twice is a no-op, a second application still initializes its own
 * client (D1).
 */
const registerApplication = (
  options: ApplicationAnalyticsOptions & {
    avoEnv: 'dev' | 'prod'
    destination?: SegmentDestination
  },
): void => {
  const {
    applicationId,
    version,
    initAvo,
    setAvoSystemProperties,
    additionalSystemProperties,
    productAdapter,
    avoEnv,
    destination = createSegmentDestination(),
  } = options

  if (initializedApplications.has(applicationId)) {
    return
  }

  if (productAdapter) {
    registerProductAdapter(applicationId, productAdapter)
  }

  if (!initAvo) {
    reportBootStage('avo', 'skipped', { applicationId })
    return
  }

  try {
    // Whatever the cookies already hold — nothing for a field GA has not
    // written yet. The gaLoaded subscription above pushes the final values
    // once the bounded wait settles.
    const gaIdentity = currentGAIdentity()

    // App-scoped properties merge BENEATH the globals — an application cannot
    // override a global (FC-183 §6).
    //
    // The shared attribution set is assembled here rather than in each app, so
    // no app has to rename segment-common's output itself. A key an app's
    // source has not declared as a system property is simply ignored by its
    // generated setSystemProperties, so passing the full set is safe before
    // every source has been regenerated.
    const systemProperties = {
      ...additionalSystemProperties,
      ...currentCommonSystemProperties(),
      applicationId,
      version,
      ...gaIdentity,
    }

    if (setAvoSystemProperties) {
      avoSystemPropertySetters.set(applicationId, setAvoSystemProperties)
    }

    initAvo(
      { env: avoEnv },
      systemProperties,
      {},
      // SegmentDestination's typed EventProperties narrows to the InitAvo
      // contract's unknown; the shapes are identical.
      destination as Parameters<typeof initAvo>[3],
    )

    initializedApplications.add(applicationId)
    avoLoaded.value = true
    // Reported after initAvo so the outcome is the final word on this stage.
    // Without the setter there is nowhere to push GA identity once GA4's
    // cookies settle, so the three ga* fields stay frozen at their init-time
    // value — usually absent (D3). Surface it at boot rather than letting it
    // show up as missing identity in the warehouse.
    if (setAvoSystemProperties) {
      reportBootStage('avo', 'ok', { applicationId })
    } else {
      reportBootStage('avo', 'skipped', {
        applicationId,
        reason: 'no setAvoSystemProperties — GA identity cannot be updated',
      })
    }
  } catch (error) {
    reportBootStage('avo', 'failed', { applicationId })
    // Non-fatal: log and swallow so an Avo failure never breaks the host application.
    errorHandler(error as Error)
  }
}

/**
 * Resolves the page's consent posture. Runs on every host registration, not
 * only the first, so a posture is never silently dropped because another
 * application happened to load the transport first.
 *
 * OneTrust wins and is sticky: once a banner owns the page, an `ungated`
 * registration cannot widen consent, and any assumed set already in place is
 * cleared. That ordering guarantee is the whole point — otherwise a composed
 * in-store widget could grant C0004 on a consumer page.
 */
const applyConsentPosture = (consent: ConsentPosture): void => {
  if (consent.mode === 'oneTrust') {
    if (pageConsentMode === 'ungated') {
      // An ungated registration got here first; a real banner outranks it.
      setAssumedConsent(undefined)
    }
    pageConsentMode = 'oneTrust'
    return
  }

  if (pageConsentMode === 'oneTrust') {
    reportBootStage('consent-script', 'skipped', {
      ignoredAssumedConsent: 'true',
      reason: 'a OneTrust host already owns this page',
    })
    return
  }

  pageConsentMode = 'ungated'

  // An application with no banner declares the consent to assume, so every
  // reader in @susu/cookie resolves against it instead of the deny-by-default
  // string. A real OptanonConsent cookie still wins over both.
  if (consent.assumedConsent) {
    setAssumedConsent(consent.assumedConsent)
  }
}

/**
 * Initializes analytics for the HOST application — the one that owns the page.
 * Call once per page. Loads the transport and the consent script, then
 * registers the host's own Avo client. A composed application mounted onto
 * this page calls {@link useAnalytics} instead (FC-183 §2–§3).
 *
 * Safe in SSR (no-op outside the browser). Initialization is non-fatal: every
 * error is logged via {@link errorHandler} and swallowed so a tracking failure
 * never breaks the host application.
 */
export function initializeAnalytics(options: InitializeAnalyticsOptions): void {
  const {
    surface,
    applicationId,
    userId,
    version,
    allowedApplicationIds,
    enabled = true,
  } = options

  if (!isBrowser() || !enabled) {
    return
  }

  try {
    validateApplicationId(applicationId, allowedApplicationIds)
  } catch (error) {
    reportBootStage('validate', 'failed', {
      applicationId: String(applicationId),
    })
    errorHandler(error as Error)
    return
  }

  // Everything the page needs follows from the surface (FC-183 §3/§5).
  const isProduction = isProductionEnvironment(surface.environment)
  const avoEnv: 'dev' | 'prod' = isProduction ? 'prod' : 'dev'
  const consent = resolveConsentPosture(surface)
  const segmentWriteKey = resolveSegmentWriteKey(
    surface.placement,
    surface.environment,
  )
  const gaMeasurementId = resolveGA4MeasurementIdForEnvironment(
    surface.environment,
  )

  // Before the transport block: a second host must still have its posture
  // resolved even though it will not construct another transport.
  applyConsentPosture(consent)

  // Only the first host owns the page and declares its context; a second
  // host's locale or customer is ignored, so the page's events stay alike.
  setPageOwner(applicationId, {
    locale: surface.locale,
    customerId: surface.customerId,
    pageType: surface.pageType,
  })

  // ── Once per page: transport + consent script (host only) ─────────────────
  if (!transportLoaded) {
    try {
      loadTransport(segmentWriteKey, consent.mode === 'oneTrust')
      // Synchronous on purpose (D1): a second initializeAnalytics call while
      // the transport is still loading must not construct a second one.
      transportLoaded = true
      reportBootStage('transport', 'ok', { consentMode: consent.mode })
    } catch (error) {
      reportBootStage('transport', 'failed')
      errorHandler(error as Error)
      return
    }

    if (consent.mode === 'oneTrust') {
      insertCookieTrackingScript(consent.oneTrustId, isProduction)
      reportBootStage('consent-script', 'ok')
    } else {
      // The deliberate, stated exception (§5): visible here and in the diff.
      reportBootStage('consent-script', 'skipped', {
        justification: consent.justification,
        approvedBy: consent.approvedBy ?? '',
        assumedConsent: consent.assumedConsent
          ? JSON.stringify(consent.assumedConsent.categoryPreferences)
          : '',
      })
    }

    // GA4 is one property per environment, the same for every application, so
    // the measurement id is resolved here rather than plumbed through each app
    // as a `trackingGaCookie`. It names the `_ga_<id>` session cookie to read;
    // GA4 itself is loaded by Segment as a destination, never by an app.
    setGaCookieId(gaMeasurementId)
    setGA4MeasurementId(gaMeasurementId)

    setGALoadedSignal(gaLoaded)
    setAvoLoadedSignal(avoLoaded)

    // Registered synchronously after load — NOT inside analytics.ready(),
    // which never fires if any device-mode destination throws during init
    // (D2). Pre-load calls are buffered by AnalyticsBrowser.
    try {
      analytics.register(integrationPlugin())
      library.value = analytics
      libraryLoaded.value = true
    } catch (error) {
      reportBootStage('transport', 'failed', { step: 'plugin-register' })
      errorHandler(error as Error)
    }

    // Identify still waits for GA identity, but on the BOUNDED gaLoaded signal
    // (2000 ms via createGAdata) rather than analytics.ready(), so a throwing
    // destination cannot suppress it indefinitely (D2). gaLoaded only settles
    // after the transport actually loads, which under OneTrust means consent
    // was granted — no consent, no identify.
    void gaPromise.then(() => {
      try {
        const consentValue = getOneTrustConsent()
        const cookieId = getGaCookieId()
        const { sessionId, sessionNumber } = getGA4SessionDataFromCookie(
          cookieId ?? '',
        )
        const identifyTraits = {
          consent_onetrust_web: consentValue,
          ga_session_id: sessionId,
          ga_client_id: getGA4ClientIdFromCookie(),
          ga_session_number: sessionNumber,
        }

        if (userId && !window.sessionStorage.getItem(IDENTITY_KEY_KNOWN)) {
          window.sessionStorage.setItem(IDENTITY_KEY_KNOWN, IDENTITY_VALUE)
          analytics.identify(userId, identifyTraits)
        } else if (!window.sessionStorage.getItem(IDENTITY_KEY_ANONYMOUS)) {
          window.sessionStorage.setItem(IDENTITY_KEY_ANONYMOUS, IDENTITY_VALUE)
          analytics.identify(identifyTraits)
        }
        reportBootStage('identify', 'ok')
      } catch (error) {
        reportBootStage('identify', 'failed')
        errorHandler(error as Error)
      }
    })

    analytics.ready(() => {
      reportBootStage('ready', 'ok')
    })
  }

  hostAvoEnv = avoEnv

  // ── Once per application: this application's own Avo client ───────────────
  registerApplication({ ...options, version, avoEnv })

  // Composed applications that registered before the host now send what
  // they buffered, in order, then send straight to Segment.
  for (const pending of pendingComposedDestinations.splice(0)) {
    try {
      pending.attach(createSegmentDestination())
    } catch (error) {
      errorHandler(error as Error)
    }
  }
}

/**
 * Registers analytics for a COMPOSED application — one mounted onto a page
 * another application owns (e.g. Size Passport inside the storefront). Call
 * once per application. Does NOT load the transport or the consent script —
 * that is the host's job — but still initializes this application's own Avo
 * client with its OWN version and system properties (FC-183 §3).
 *
 * Safe to call before the host has initialized: the Avo client initializes
 * at once, and its calls buffer until the host's initializeAnalytics runs.
 */
export function useAnalytics(options: UseAnalyticsOptions): void {
  registerComposedApplication(options)
}

/**
 * The composed registration. `avoEnvHint` is the composed surface's own
 * environment, used only when no host has initialized yet.
 */
function registerComposedApplication(
  options: UseAnalyticsOptions,
  avoEnvHint?: 'dev' | 'prod',
): void {
  if (!isBrowser()) {
    return
  }

  try {
    validateApplicationId(options.applicationId, options.allowedApplicationIds)
  } catch (error) {
    reportBootStage('validate', 'failed', {
      applicationId: String(options.applicationId),
    })
    errorHandler(error as Error)
    return
  }

  // Before the host initializes there is no transport, but the Avo client is
  // initialized at once so a direct Avo call never throws. Its calls buffer
  // until initializeAnalytics attaches the real destination. The generated
  // initAvo runs once, so its env cannot follow the host's later: use the
  // surface's own env, else 'prod', which never shows the Avo web debugger
  // to a customer.
  if (hostAvoEnv === undefined) {
    const destination = createBufferedDestination()
    pendingComposedDestinations.push(destination)
    registerApplication({
      ...options,
      avoEnv: avoEnvHint ?? 'prod',
      destination,
    })
    // A registration that never completes means the host never initialized —
    // a misconfiguration that would otherwise silently swallow every event
    // this application fires (review finding, PR 6822). Surface it, bounded.
    setTimeout(() => {
      if (
        hostAvoEnv === undefined &&
        pendingComposedDestinations.includes(destination)
      ) {
        errorHandler(
          new Error(
            `[analytics-client] useAnalytics('${String(options.applicationId)}') is still waiting for a host initializeAnalytics after ${COMPOSED_REGISTRATION_WARN_MS} ms — its first ${BUFFERED_DESTINATION_LIMIT} Avo calls are buffered and will not send until a host initializes.`,
          ),
        )
        reportBootStage('avo', 'timeout', {
          applicationId: String(options.applicationId),
        })
      }
    }, COMPOSED_REGISTRATION_WARN_MS)
    return
  }

  registerApplication({ ...options, avoEnv: hostAvoEnv })
}

/**
 * Registers analytics for a surface that is a host in one mount and composed
 * in another — Size Passport inside the storefront vs standalone, Customer Hub
 * in RP vs embedded. Routes on `surface.ownsPage` so the decision is made
 * where it is known, at the mount point, rather than guessed per package.
 *
 * A composed registration drops the surface's transport and consent posture:
 * the host owns those, so a composed app's own write key would never be used.
 * Its environment is kept only as the Avo env hint, used when the composed
 * client initializes before any host.
 */
export function registerAnalytics(options: RegisterAnalyticsOptions): void {
  const { surface, userId, enabled, ...application } = options

  if (surface.ownsPage) {
    initializeAnalytics({
      ...application,
      surface,
      userId,
      enabled,
    })
    return
  }

  // The kill switch has to hold in both roles. A composed surface passing
  // `enabled: false` would otherwise still register its Avo client and fire
  // events, because useAnalytics never sees the flag.
  if (enabled === false) {
    return
  }

  registerComposedApplication(
    application,
    isProductionEnvironment(surface.environment) ? 'prod' : 'dev',
  )
}
